import { Component, Input, Output, EventEmitter, forwardRef, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef as CDR } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FieldSchema } from '../../core/models/field-schema.model';
import { getInputTypeFormatHint } from '../../core/validators/validation-messages';

/**
 * Dynamic common input component
 * Custom styled input field for dynamic form system
 */
@Component({
  selector: 'app-dcf-common-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CommonInputComponent),
      multi: true
    }
  ],
  templateUrl: './common-input.component.html',
  styleUrls: ['./common-input.component.scss']
})
export class CommonInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @ViewChild('inputElement', { static: false }) inputElementRef!: ElementRef<HTMLInputElement>;

  @Input() fieldSchema!: FieldSchema;
  @Input() formControl!: FormControl;

  @Output() valueChange = new EventEmitter<any>();
  @Output() blur = new EventEmitter<void>();
  @Output() focus = new EventEmitter<void>();

  value: any = '';
  disabled = false;
  visible = true;

  private destroy$ = new Subject<void>();
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: CDR) {}

  ngOnInit(): void {
    if (this.formControl) {
      this.disabled = this.formControl.disabled;

      // Sync with form control
      this.formControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          this.value = value;
        });

      // React to enable/disable
      this.formControl.statusChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.disabled = this.formControl.disabled;
          this.cdr.detectChanges();
        });
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.fieldSchema?.inputType === 'number' ?
      (input.value ? Number(input.value) : null) :
      input.value;

    this.value = value;

    // Update the FormControl directly to trigger validators
    if (this.formControl) {
      this.formControl.setValue(value);
      this.formControl.markAsDirty();
    }

    this.onChange(value);
    this.valueChange.emit(value);
    this.cdr.detectChanges();
  }

  onBlur(): void {
    this.onTouched();

    // Mark as touched for validation display
    if (this.formControl) {
      this.formControl.markAsTouched();
    }

    // Clear invalid email on blur
    if (this.fieldSchema?.inputType === 'email' && this.formControl?.invalid && this.value) {

      // Clear the value
      this.value = '';
      this.onChange('');

      // Update form control without emitting event
      if (this.formControl) {
        this.formControl.setValue('', { emitEvent: false });
        this.formControl.markAsPristine();
        this.formControl.markAsUntouched();
      }

      // Force update the DOM input element
      if (this.inputElementRef && this.inputElementRef.nativeElement) {
        this.inputElementRef.nativeElement.value = '';
      }
    }

    this.blur.emit();
  }

  onFocus(): void {
    this.focus.emit();
  }

  getErrorMessages(): string[] {
    if (!this.formControl || !this.formControl.errors) {
      return [];
    }

    const errors: string[] = [];
    const errorObj = this.formControl.errors;

    // Check for custom error messages from field schema
    if (this.fieldSchema?.errorMessages) {
      for (const key in errorObj) {
        if (this.fieldSchema.errorMessages[key]) {
          errors.push(this.fieldSchema.errorMessages[key]);
        } else {
          errors.push(this.getDefaultErrorMessage(key, errorObj[key]));
        }
      }
    } else {
      // Use default error messages
      for (const key in errorObj) {
        errors.push(this.getDefaultErrorMessage(key, errorObj[key]));
      }
    }

    return errors;
  }

  private getDefaultErrorMessage(errorKey: string, errorValue: any): string {
    const label = this.fieldSchema?.label || 'This field';

    switch (errorKey) {
      case 'required':
        return `${label} is required`;
      case 'email':
        return 'Please enter a valid email address';
      case 'minlength':
        return `Minimum length is ${errorValue.requiredLength} characters`;
      case 'maxlength':
        return `Maximum length is ${errorValue.requiredLength} characters`;
      case 'min':
        return `Minimum value is ${errorValue.min}`;
      case 'max':
        return `Maximum value is ${errorValue.max}`;
      case 'pattern':
        return `${label} format is invalid`;
      default:
        return `${label} is invalid`;
    }
  }

  hasContent(): boolean {
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  shouldShowErrors(): boolean {
    if (!this.formControl) {
      return false;
    }

    // For email fields, show errors immediately when invalid and has content
    if (this.fieldSchema?.inputType === 'email') {
      const showError = this.formControl.invalid && this.hasContent();
      return showError;
    }

    // For other fields, show errors only after touched
    return this.formControl.invalid && this.formControl.touched;
  }

  shouldShowHelpText(): boolean {
    // Don't show help text if there are errors to display
    if (this.shouldShowErrors()) {
      return false;
    }

    // Show format hint for email when focused or has invalid content
    if (this.fieldSchema?.inputType === 'email' && this.hasContent() && !this.formControl?.valid) {
      return true;
    }

    // Show regular help text if available
    return !!this.fieldSchema?.helpText;
  }

  getHelpTextOrFormatHint(): string {
    // Show format hint for specific input types when there's invalid content
    if (this.hasContent() && !this.formControl?.valid && this.fieldSchema?.inputType) {
      const formatHint = getInputTypeFormatHint(this.fieldSchema.inputType);
      if (formatHint) {
        return formatHint;
      }
    }

    return this.fieldSchema?.helpText || '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
